import {Coordinate} from '../../models/types';

/**
 * Utility functions for working with astrological coordinates
 */

/**
 * Converts coordinate object to decimal degrees
 * @param coord - Coordinate object with degrees, minutes, seconds
 * @returns Decimal degrees
 */
export function coordinateToDecimal(coord: Coordinate): number {
    return coord.degrees + (coord.minutes / 60) + (coord.seconds / 3600);
}

/**
 * Alias for coordinateToDecimal - converts coordinate to decimal longitude
 * Used by calculation engines for longitude-based operations
 * @param coord - Coordinate object with degrees, minutes, seconds
 * @returns Decimal degrees (longitude)
 */
export const coordinateToLongitude = coordinateToDecimal;

/**
 * Converts decimal degrees to coordinate object with degrees within sign (0-29)
 * @param decimal - Decimal degrees (absolute position 0-360)
 * @returns Coordinate object with degrees within the current sign
 */
export function decimalToCoordinate(decimal: number): Coordinate {
    // Ensure the decimal is positive and less than 360
    const normalizedDecimal = ((decimal % 360) + 360) % 360;

    // Get degrees within the current sign (0-29)
    const degreesWithinSign = normalizedDecimal % 30;

    const totalSeconds = degreesWithinSign * 3600;
    const degrees = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds - degrees * 3600) / 60);
    const seconds = parseFloat((totalSeconds - degrees * 3600 - minutes * 60).toFixed(2));

    return {degrees, minutes, seconds};
}

/**
 * Converts decimal degrees to coordinate object with absolute degrees (0-359)
 * @param decimal - Decimal degrees
 * @returns Coordinate object with absolute degrees
 */
export function decimalToAbsoluteCoordinate(decimal: number): Coordinate {
    // Ensure the decimal is positive and less than 360
    const normalizedDecimal = ((decimal % 360) + 360) % 360;

    const totalSeconds = Math.round(normalizedDecimal * 3600);
    const degrees = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {degrees, minutes, seconds};
}

/**
 * Formats coordinate to string representation
 * @param coord - Coordinate object
 * @returns Formatted string (e.g., "15° 30' 45"")
 */
export function formatCoordinate(coord: Coordinate): string {
    const truncatedSeconds = Math.floor(coord.seconds * 100) / 100;
    return `${coord.degrees}° ${coord.minutes}' ${truncatedSeconds.toFixed(2)}"`;
}

/**
 * Formats decimal degrees to string representation
 * @param decimal - Decimal degrees
 * @returns Formatted string (e.g., "15° 30' 45"")
 */
export function formatDecimalDegrees(decimal: number): string {
    return formatCoordinate(decimalToCoordinate(decimal));
}

/**
 * Normalizes a decimal degree value to the range [0, 360)
 * @param decimal - Decimal degrees
 * @returns Normalized decimal degrees
 */
export function normalizeDecimalDegrees(decimal: number): number {
    return ((decimal % 360) + 360) % 360;
}

/**
 * Gets the rashi (zodiac sign) index (0-11) for a given decimal degree
 * @param decimal - Decimal degrees
 * @returns Rashi index (0-11)
 */
export function getRashiIndex(decimal: number): number {
    const normalized = normalizeDecimalDegrees(decimal);
    return Math.floor(normalized / 30);
}

/**
 * Gets the nakshatra (lunar mansion) index (0-26) for a given decimal degree
 * @param decimal - Decimal degrees
 * @returns Nakshatra index (0-26)
 */
export function getNakshatraIndex(decimal: number): number {
    const normalized = normalizeDecimalDegrees(decimal);
    // 27 nakshatras in 360 degrees = 13.333... degrees per nakshatra
    return Math.floor(normalized / (360 / 27));
}

/**
 * Gets the pada (quarter) index (0-3) within a nakshatra for a given decimal degree
 * @param decimal - Decimal degrees
 * @returns Pada index (0-3)
 */
export function getPadaIndex(decimal: number): number {
    const normalized = normalizeDecimalDegrees(decimal);
    const nakshatraSize = 360 / 27;  // Size of one nakshatra in degrees
    const padaSize = nakshatraSize / 4;  // Size of one pada in degrees

    // Get position within the nakshatra
    const nakshatraIndex = getNakshatraIndex(normalized);
    const nakshatraStart = nakshatraIndex * nakshatraSize;
    const positionInNakshatra = normalized - nakshatraStart;

    // Calculate pada index
    return Math.floor(positionInNakshatra / padaSize);
}

/**
 * Gets the navamsa (one-ninth division) index (0-8) within a rashi for a given decimal degree
 * @param decimal - Decimal degrees
 * @returns Navamsa index (0-8)
 */
export function getNavamsaIndex(decimal: number): number {
    const normalized = normalizeDecimalDegrees(decimal);
    const rashiSize = 30;  // Size of one rashi in degrees
    const navamsaSize = rashiSize / 9;  // Size of one navamsa in degrees

    // Get position within the rashi
    const rashiIndex = getRashiIndex(normalized);
    const rashiStart = rashiIndex * rashiSize;
    const positionInRashi = normalized - rashiStart;

    // Calculate navamsa index
    return Math.floor(positionInRashi / navamsaSize);
}

/**
 * Gets the absolute navamsa (D9) index (0-107) for a given decimal degree
 * @param decimal - Decimal degrees
 * @returns Absolute navamsa index (0-107)
 */
export function getAbsoluteNavamsaIndex(decimal: number): number {
    const normalized = normalizeDecimalDegrees(decimal);
    const navamsaSize = 30 / 9;  // Size of one navamsa in degrees

    // Calculate absolute navamsa index
    return Math.floor(normalized / navamsaSize);
}

/**
 * Gets the dwadasamsa (one-twelfth division) index (0-11) within a rashi for a given decimal degree
 * @param decimal - Decimal degrees
 * @returns Dwadasamsa index (0-11)
 */
export function getDwadasamsaIndex(decimal: number): number {
    const normalized = normalizeDecimalDegrees(decimal);
    const rashiSize = 30;  // Size of one rashi in degrees
    const dwadasamsaSize = rashiSize / 12;  // Size of one dwadasamsa in degrees

    // Get position within the rashi
    const rashiIndex = getRashiIndex(normalized);
    const rashiStart = rashiIndex * rashiSize;
    const positionInRashi = normalized - rashiStart;

    // Calculate dwadasamsa index
    return Math.floor(positionInRashi / dwadasamsaSize);
}

/**
 * Calculates the distance in degrees between two points on the zodiac
 * @param from - Starting position in decimal degrees
 * @param to - Ending position in decimal degrees
 * @param clockwise - Direction of measurement (default: true)
 * @returns Distance in decimal degrees
 */
export function calculateZodiacDistance(
    from: number,
    to: number,
    clockwise: boolean = true
): number {
    const normalizedFrom = normalizeDecimalDegrees(from);
    const normalizedTo = normalizeDecimalDegrees(to);

    if (clockwise) {
        return normalizedTo >= normalizedFrom
            ? normalizedTo - normalizedFrom
            : 360 - normalizedFrom + normalizedTo;
    } else {
        return normalizedFrom >= normalizedTo
            ? normalizedFrom - normalizedTo
            : 360 - normalizedTo + normalizedFrom;
    }
}

/**
 * Parse DMS (Degrees/Minutes/Seconds) or decimal string to decimal degrees
 * Supports both formats:
 * - Decimal: "214.697278"
 * - DMS: "214° 38' 39.38""
 * @param dmsString - String representation of degrees
 * @returns Decimal degrees, or 0 if parsing fails
 */
export function parseDMSToDecimal(dmsString: string): number {
    if (!dmsString || typeof dmsString !== 'string') {
        return 0;
    }

    // Check if already decimal number (e.g., "214.697278")
    const decimalMatch = dmsString.match(/^(\d+(?:\.\d+)?)$/);
    if (decimalMatch) {
        return parseFloat(decimalMatch[1]);
    }

    // Try DMS format (e.g., "214° 38' 39.38"")
    const dmsMatch = dmsString.match(/(\d+)°\s*(\d+)'\s*(\d+(?:\.\d+)?)\s*"/);
    if (!dmsMatch) {
        return 0;
    }

    const [, degrees, minutes, seconds] = dmsMatch;
    return coordinateToDecimal({
        degrees: parseInt(degrees, 10),
        minutes: parseInt(minutes, 10),
        seconds: parseFloat(seconds)
    });
}

/**
 * Parse DMS string to Coordinate object
 * Example: "112° 09' 31.27"" → { degrees: 112, minutes: 9, seconds: 31.27 }
 * @param dmsString - DMS formatted string
 * @returns Coordinate object, or zeroed coordinate if parsing fails
 */
export function parseDMSToCoordinate(dmsString: string): Coordinate {
    const match = dmsString.match(/(\d+)°\s*(\d+)'\s*(\d+(?:\.\d+)?)\s*"/);
    if (!match) {
        return { degrees: 0, minutes: 0, seconds: 0 };
    }

    const [, degrees, minutes, seconds] = match;
    return {
        degrees: parseInt(degrees, 10),
        minutes: parseInt(minutes, 10),
        seconds: parseFloat(seconds)
    };
}

/**
 * Parse a degree string in various formats to decimal degrees
 * @param degreeString - String representation of degrees (e.g., "15° 30' 45"", "15 Aries 30' 45"")
 * @returns Decimal degrees
 */
export function parseDegreeString(degreeString: string): number {
    // Try pattern like "15° 30' 45""
    const simplePattern = /(\d+)\s*°\s*(\d+)\s*'\s*(\d+(\.\d+)?)\s*"/;
    const simpleMatch = degreeString.match(simplePattern);

    if (simpleMatch) {
        const [, degrees, minutes, seconds] = simpleMatch;
        return parseInt(degrees, 10) + (parseInt(minutes, 10) / 60) + (parseFloat(seconds) / 3600);
    }

    // Try pattern like "15 Aries 30' 45""
    const signPattern = /(\d+)\s+(\w+)\s+(\d+)\s*'\s*(\d+(\.\d+)?)\s*"/;
    const signMatch = degreeString.match(signPattern);

    if (signMatch) {
        const [, degrees, sign, minutes, seconds] = signMatch;

        // Map traditional names and abbreviations to zodiac indices
        const signMap: Record<string, number> = {
            'Aries': 0, 'Ar': 0, 'Mesh': 0, 'Mesha': 0,
            'Taurus': 1, 'Ta': 1, 'Vrish': 1, 'Vrishabha': 1,
            'Gemini': 2, 'Ge': 2, 'Mith': 2, 'Mithuna': 2,
            'Cancer': 3, 'Cn': 3, 'Kark': 3, 'Karka': 3,
            'Leo': 4, 'Le': 4, 'Simh': 4, 'Simha': 4,
            'Virgo': 5, 'Vi': 5, 'Kanya': 5,
            'Libra': 6, 'Li': 6, 'Tula': 6,
            'Scorpio': 7, 'Sc': 7, 'Vrischik': 7, 'Vrishchika': 7,
            'Sagittarius': 8, 'Sa': 8, 'Dhanu': 8,
            'Capricorn': 9, 'Cp': 9, 'Makar': 9, 'Makara': 9,
            'Aquarius': 10, 'Aq': 10, 'Kumbh': 10, 'Kumbha': 10,
            'Pisces': 11, 'Pi': 11, 'Meen': 11, 'Meena': 11
        };

        const signIndex = signMap[sign] ?? 0;  // Default to Aries if sign not found
        const basePosition = signIndex * 30;

        return basePosition +
            parseInt(degrees, 10) +
            (parseInt(minutes, 10) / 60) +
            (parseFloat(seconds) / 3600);
    }

    // If no pattern matches, return 0
    return 0;
}
